import {gql, type TypedDocumentNode} from '@apollo/client';
import {useQuery} from '@apollo/client/react';
import {useMemo} from 'react';

import type {_Array, _Object} from '../../Paramut';
import type {
  GetCitiesByNameQuery,
  GetCitiesByNameQueryVariables,
  GetFavoriteCitiesQuery,
  GetFavoriteCitiesQueryVariables,
} from './useCitiesByName.generated';

export type UseCitiesByNameProps = {
  favoriteIds?: number[];
  name?: string;
};

export type UseCitiesByNameResult = {
  favorites: Cities | undefined | null;
  search: Cities | undefined | null;
};

export type Cities<Mut extends boolean = false> = _Array<City<Mut>>;

export type City<Mut extends boolean = false> = _Object<
  {
    id: number;
    name: string;
  },
  Mut
>;

export default function useCitiesByName(
  props: UseCitiesByNameProps,
): UseCitiesByNameResult {
  const {favoriteIds: _favoriteIds, name} = props;

  const favoriteIds = useMemo(
    () => _favoriteIds?.map(_ => _.toString(10)) ?? [],
    [_favoriteIds],
  );

  const {data: favoritesData} = useQuery(GET_FAVORITE_CITIES, {
    variables: {favoriteIds},
  });

  const {data: searchData} = useQuery(GET_CITIES_BY_NAME, {
    variables: {name, excludeIds: name ? undefined : favoriteIds},
  });

  const favorites = favoritesData?.Cities?.docs;
  const search = searchData?.Cities?.docs;

  if (name) {
    return {favorites: null, search};
  }

  return {favorites, search};
}

const GET_FAVORITE_CITIES: TypedDocumentNode<
  GetFavoriteCitiesQuery,
  GetFavoriteCitiesQueryVariables
> = gql`
  query GetFavoriteCities($favoriteIds: [String]) {
    Cities(
      where: {idString: {in: $favoriteIds}}
      pagination: false
      sort: "name"
    ) {
      docs {
        id
        name
      }
    }
  }
`;

const GET_CITIES_BY_NAME: TypedDocumentNode<
  GetCitiesByNameQuery,
  GetCitiesByNameQueryVariables
> = gql`
  query GetCitiesByName($name: String, $excludeIds: [String]) {
    Cities(
      where: {
        AND: [{name: {contains: $name}}, {idString: {not_in: $excludeIds}}]
      }
      pagination: false
      sort: "name"
    ) {
      docs {
        id
        name
      }
    }
  }
`;
